import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export type OpeningStatus = 'opened' | 'holiday' | 'weekend';

export interface OpeningDay {
  date: string;
  status: OpeningStatus;
  label: string;
  remark: string;
  marked: boolean;
}

export interface OpeningDaysResponse {
  data: OpeningDay[];
}

export const AttendanceApi = createApi({
  reducerPath: 'AttendanceApi',
  baseQuery,
  tagTypes: ['StudentAttendance', 'Closures', 'Openings', 'StaffAttendanceQR'],
  endpoints: builder => ({
    getOpeningDays: builder.query<OpeningDaysResponse, void>({
      query: () => `/attendance/openings`,
      providesTags: ['Openings'],
    }),

    getClassesToMark: builder.query<any, { date: string }>({
      query: ({ date }) => `/attendance/openings/${date}/classes`,
    }),

    getStudentsAttendanceDetails: builder.query<
      any,
      { date: string; classId: string }
    >({
      query: ({ date, classId }) =>
        `/attendance/openings/${date}/classes/${classId}`,
    }),

    getStaffAttendanceDetail: builder.query<any, { date: string }>({
      query: ({ date }) => `/attendance/openings/${date}/staff`,
    }),

    updateStudentAttendance: builder.mutation<any, { updates: any }>({
      query: ({ updates }) => ({
        url: `/attendance/openings`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    bulkUpdateStudentAttendance: builder.mutation<any, { updates: any }>({
      query: ({ updates }) => ({
        url: `/attendance/openings/bulk-mark`,
        method: 'POST',
        body: updates,
      }),
    }),
    getStudentAttendanceSummary: builder.query<any, { studentId: string }>({
      query: ({ studentId }) => `/attendance/students/${studentId}/summary`,
    }),
    getStaffAttendanceSummary: builder.query<any, { staffId: string }>({
      query: ({ staffId }) => `/attendance/staff/${staffId}/summary`,
    }),

    // Attendance History endpoints
    getStudentAttendanceHistory: builder.query<
      any,
      { sessionId: string; studentId: string }
    >({
      query: ({ sessionId, studentId }) =>
        `/attendance/sessions/${sessionId}/students/${studentId}/history`,
    }),

    getStaffAttendanceHistory: builder.query<
      any,
      { sessionId: string; staffId: string }
    >({
      query: ({ sessionId, staffId }) =>
        `/attendance/sessions/${sessionId}/staff/${staffId}/history`,
    }),

    // School Closures endpoints
    getClosures: builder.query<any, void>({
      query: () => `/attendance/closures`,
      providesTags: ['Closures'],
    }),

    createClosure: builder.mutation<
      any,
      { name: string; fromDate: string; toDate: string }
    >({
      query: closure => ({
        url: `/attendance/closures`,
        method: 'POST',
        body: closure,
      }),
      invalidatesTags: ['Closures', 'Openings'],
    }),

    updateClosure: builder.mutation<
      any,
      { id: string; name: string; fromDate: string; toDate: string }
    >({
      query: ({ id, ...closure }) => ({
        url: `/attendance/closures/${id}`,
        method: 'PATCH',
        body: closure,
      }),
      invalidatesTags: ['Closures', 'Openings'],
    }),

    deleteClosure: builder.mutation<any, string>({
      query: id => ({
        url: `/attendance/closures/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Closures', 'Openings'],
    }),

    getStaffAttendanceQR: builder.query<any, void>({
      query: () => `/attendance/staff/qr`,
      providesTags: ['StaffAttendanceQR'],
    }),

    generateStaffAttendanceQR: builder.mutation<
      any,
      { latitude: number; longitude: number }
    >({
      query: data => ({
        url: `/attendance/staff/qr`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StaffAttendanceQR'],
    }),

    markStaffSelfAttendance: builder.mutation<
      any,
      {
        qrToken: string;
        latitude: number;
        longitude: number;
        deviceId: string;
      }
    >({
      query: data => ({
        url: `/attendance/staff/mark-self`,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetOpeningDaysQuery,
  useGetClassesToMarkQuery,
  useGetStudentsAttendanceDetailsQuery,
  useGetStaffAttendanceDetailQuery,
  useUpdateStudentAttendanceMutation,
  useGetStudentAttendanceSummaryQuery,
  useGetStaffAttendanceSummaryQuery,
  useGetStudentAttendanceHistoryQuery,
  useGetStaffAttendanceHistoryQuery,
  useGetClosuresQuery,
  useCreateClosureMutation,
  useUpdateClosureMutation,
  useDeleteClosureMutation,
  useGetStaffAttendanceQRQuery,
  useGenerateStaffAttendanceQRMutation,
  useMarkStaffSelfAttendanceMutation,
  useBulkUpdateStudentAttendanceMutation,
} = AttendanceApi;
